/**
 * Offline Database for GCHL Live Scoring
 * Uses Dexie.js (IndexedDB wrapper) for local storage
 *
 * SECURITY: All user data is scoped to userId to prevent cross-user data exposure
 */
import Dexie from 'dexie';

// Maximum retry attempts for failed syncs
const MAX_RETRY_COUNT = 3;

// Create database instance
export const db = new Dexie('gchl-livescoring');

// Define schema - v3 adds ctpQueue so CTP set/clear survive flaky course
// internet the same way scores do (queue → retry → sync).
db.version(3).stores({
  syncQueue: '++id, eventId, playerId, holeNumber, status, timestamp, userId, [eventId+playerId+holeNumber], [userId+status]',
  // Pending CTP actions to sync — one row per event+hole, latest intent wins
  ctpQueue: '++id, eventId, holeNumber, status, timestamp, userId, [eventId+holeNumber], [userId+status]',
  cachedEvents: 'id, date, cachedAt, userId',
  cachedGroups: '[eventId+groupNumber], eventId, cachedAt, userId',
  cachedPlayers: '++id, eventId, playerId, cachedAt, userId',
  cachedScorecards: 'eventId, cachedAt, userId',
  preferences: 'key',
});

// Define schema - v2 adds userId for user scoping
db.version(2).stores({
  // Pending scores to sync - scoped to userId, unique by event/player/hole
  syncQueue: '++id, eventId, playerId, holeNumber, status, timestamp, userId, [eventId+playerId+holeNumber], [userId+status]',
  // Cached event data for offline access
  cachedEvents: 'id, date, cachedAt, userId',
  // Cached groups/pairings per event
  cachedGroups: '[eventId+groupNumber], eventId, cachedAt, userId',
  // Cached player data with quotas
  cachedPlayers: '++id, eventId, playerId, cachedAt, userId',
  // Cached scorecard data (par, handicap per hole)
  cachedScorecards: 'eventId, cachedAt, userId',
  // App preferences - not user-specific
  preferences: 'key',
}).upgrade(tx => {
  // Migration: existing data without userId will be cleared on next login
  // This is safer than keeping orphaned data
});

// Keep v1 for backwards compatibility during upgrade
db.version(1).stores({
  syncQueue: '++id, eventId, playerId, holeNumber, status, timestamp, [eventId+playerId+holeNumber]',
  cachedEvents: 'id, date, cachedAt',
  cachedGroups: '[eventId+groupNumber], eventId, cachedAt',
  cachedPlayers: '++id, eventId, playerId, cachedAt',
  cachedScorecards: 'eventId, cachedAt',
  preferences: 'key',
});

// Sync status enum
export const SyncStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  FAILED: 'failed',
};

// Current user ID - must be set before any operations
let currentUserId = null;

/**
 * Set the current user ID for all database operations
 * MUST be called on login before any other operations
 */
export function setCurrentUser(userId) {
  currentUserId = userId;
}

/**
 * Get the current user ID
 */
export function getCurrentUser() {
  return currentUserId;
}

/**
 * Validate that a user is set before operations
 */
function requireUser() {
  if (!currentUserId) {
    throw new Error('No user set. Call setCurrentUser() before database operations.');
  }
  return currentUserId;
}

/**
 * Migrate existing localStorage queue to IndexedDB (one-time)
 */
export async function migrateFromLocalStorage() {
  const migrated = await db.preferences.get('localStorageMigrated');
  if (migrated?.value) return;

  try {
    const oldQueue = localStorage.getItem('scoreSyncQueue');
    if (oldQueue && currentUserId) {
      const scores = JSON.parse(oldQueue);
      for (const score of scores) {
        await queueScore({
          eventId: score.eventId,
          playerId: score.playerId,
          holeNumber: score.holeNumber,
          strokes: score.strokes,
          isPickup: score.isPickup,
        });
      }
      // Clear old localStorage queue after migration
      localStorage.removeItem('scoreSyncQueue');
    }
    await db.preferences.put({ key: 'localStorageMigrated', value: true });
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Failed to migrate localStorage queue:', error);
    }
  }
}

/**
 * Queue a score for sync (upserts by event/player/hole)
 */
export async function queueScore(scoreData) {
  const userId = requireUser();
  const { eventId, playerId, holeNumber } = scoreData;

  // Find existing entry for this score (scoped to current user)
  const existing = await db.syncQueue
    .where('[eventId+playerId+holeNumber]')
    .equals([eventId, playerId, holeNumber])
    .and(item => item.userId === userId)
    .first();

  const now = Date.now();

  if (existing) {
    // Update existing entry (upsert)
    return db.syncQueue.update(existing.id, {
      ...scoreData,
      status: SyncStatus.PENDING,
      updatedAt: now,
      retryCount: 0,
      error: null,
    });
  }

  // Create new entry
  return db.syncQueue.add({
    ...scoreData,
    userId,
    status: SyncStatus.PENDING,
    timestamp: now,
    updatedAt: now,
    retryCount: 0,
  });
}

/**
 * Get all pending scores (excludes failed items that exceeded retry limit)
 */
export async function getPendingScores() {
  const userId = requireUser();
  return db.syncQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.PENDING])
    .toArray();
}

/**
 * Get failed scores that are eligible for retry (under retry limit)
 */
export async function getRetryableScores() {
  const userId = requireUser();
  return db.syncQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .filter(s => (s.retryCount || 0) < MAX_RETRY_COUNT)
    .toArray();
}

/**
 * Get pending count (includes retryable failed items)
 */
export async function getPendingCount() {
  const userId = requireUser();
  const pending = await db.syncQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.PENDING])
    .count();

  const retryable = await db.syncQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .filter(s => (s.retryCount || 0) < MAX_RETRY_COUNT)
    .count();

  return pending + retryable;
}

/**
 * Get count of permanently failed items (exceeded retry limit)
 */
export async function getFailedCount() {
  const userId = requireUser();
  return db.syncQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .filter(s => (s.retryCount || 0) >= MAX_RETRY_COUNT)
    .count();
}

/**
 * Mark a queued item as syncing (works on either syncQueue or ctpQueue)
 */
export async function markSyncing(id, table = db.syncQueue) {
  return table.update(id, { status: SyncStatus.SYNCING });
}

/**
 * Mark a queued item as synced
 */
export async function markSynced(id, table = db.syncQueue) {
  return table.update(id, {
    status: SyncStatus.SYNCED,
    syncedAt: Date.now(),
  });
}

/**
 * Mark a queued item as failed with error
 * Returns true if item can be retried, false if it exceeded retry limit
 */
export async function markFailed(id, error, table = db.syncQueue) {
  const entry = await table.get(id);
  const retryCount = (entry?.retryCount || 0) + 1;
  const canRetry = retryCount < MAX_RETRY_COUNT;

  await table.update(id, {
    status: SyncStatus.FAILED,
    error: error?.message || String(error) || 'Unknown error',
    retryCount,
    lastAttempt: Date.now(),
  });

  return canRetry;
}

/**
 * Clear synced scores older than 24 hours
 */
export async function cleanupSynced() {
  const userId = requireUser();
  const cutoff = Date.now() - (24 * 60 * 60 * 1000);
  return db.syncQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.SYNCED])
    .filter(s => s.timestamp < cutoff)
    .delete();
}

// ---------------------------------------------------------------------------
// CTP action queue — mirrors the score queue so CTP set/clear survive offline.
// One row per event+hole; the latest intent (set <player> | clear) wins, so a
// burst of taps on the same hole collapses to a single action to sync.
// ---------------------------------------------------------------------------

/**
 * Queue a CTP action for sync (upserts by event/hole — latest action wins).
 * Runs in a transaction so two concurrent callers (e.g. two open tabs) can't
 * both read "no existing row" and insert duplicates. Returns the row id and a
 * monotonic `rev` token: each re-queue bumps `rev`, so a sync that started
 * before a re-queue can detect it raced and avoid marking the newer intent done.
 * @param {{eventId:number, holeNumber:number, action:'set'|'clear', playerId?:number|null, distanceInches?:number|null}} ctpAction
 * @returns {Promise<{id:number, rev:number}>}
 */
export async function queueCtpAction(ctpAction) {
  const userId = requireUser();
  const { eventId, holeNumber, action, playerId = null, distanceInches = null } = ctpAction;
  const now = Date.now();

  return db.transaction('rw', db.ctpQueue, async () => {
    const existing = await db.ctpQueue
      .where('[eventId+holeNumber]')
      .equals([eventId, holeNumber])
      .and(item => item.userId === userId)
      .first();

    if (existing) {
      const rev = (existing.rev || 0) + 1;
      await db.ctpQueue.update(existing.id, {
        action,
        playerId,
        distanceInches,
        status: SyncStatus.PENDING,
        updatedAt: now,
        rev,
        retryCount: 0,
        error: null,
      });
      return { id: existing.id, rev };
    }

    const id = await db.ctpQueue.add({
      eventId,
      holeNumber,
      action,
      playerId,
      distanceInches,
      userId,
      status: SyncStatus.PENDING,
      timestamp: now,
      updatedAt: now,
      rev: 1,
      retryCount: 0,
    });
    return { id, rev: 1 };
  });
}

/**
 * Mark a CTP action synced, but only if it hasn't been re-queued since the
 * attempt started (rev unchanged). Returns true if applied.
 */
export async function markCtpSynced(id, expectedRev) {
  return db.transaction('rw', db.ctpQueue, async () => {
    const row = await db.ctpQueue.get(id);
    if (!row || (expectedRev != null && row.rev !== expectedRev)) return false;
    await db.ctpQueue.update(id, { status: SyncStatus.SYNCED, syncedAt: Date.now() });
    return true;
  });
}

/**
 * Mark a CTP action failed, but only if it hasn't been re-queued since the
 * attempt started. Returns { applied, canRetry }.
 */
export async function markCtpFailed(id, error, expectedRev) {
  return db.transaction('rw', db.ctpQueue, async () => {
    const row = await db.ctpQueue.get(id);
    if (!row || (expectedRev != null && row.rev !== expectedRev)) {
      return { applied: false, canRetry: true };
    }
    const retryCount = (row.retryCount || 0) + 1;
    const canRetry = retryCount < MAX_RETRY_COUNT;
    await db.ctpQueue.update(id, {
      status: SyncStatus.FAILED,
      error: error?.message || String(error) || 'Unknown error',
      retryCount,
      lastAttempt: Date.now(),
    });
    return { applied: true, canRetry };
  });
}

/**
 * Get CTP actions that still need syncing: pending, retryable failed, and any
 * left SYNCING by a prior run that was interrupted mid-flight (e.g. the phone
 * backgrounded the tab) — those would otherwise be stranded forever.
 */
export async function getSyncableCtps() {
  const userId = requireUser();
  const pending = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.PENDING])
    .toArray();
  const syncing = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.SYNCING])
    .toArray();
  const retryable = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .filter(c => (c.retryCount || 0) < MAX_RETRY_COUNT)
    .toArray();
  return [...pending, ...syncing, ...retryable];
}

/**
 * Count of CTP actions still needing sync (pending + in-flight + retryable).
 */
export async function getCtpPendingCount() {
  const userId = requireUser();
  const pending = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.PENDING])
    .count();
  const syncing = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.SYNCING])
    .count();
  const retryable = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .filter(c => (c.retryCount || 0) < MAX_RETRY_COUNT)
    .count();
  return pending + syncing + retryable;
}

/**
 * Count of permanently failed CTP actions (exceeded retry limit).
 */
export async function getCtpFailedCount() {
  const userId = requireUser();
  return db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .filter(c => (c.retryCount || 0) >= MAX_RETRY_COUNT)
    .count();
}

/**
 * Get the locally-queued CTP action for a hole (for optimistic UI).
 */
export async function getLocalCtp(eventId, holeNumber) {
  const userId = requireUser();
  const entry = await db.ctpQueue
    .where('[eventId+holeNumber]')
    .equals([eventId, holeNumber])
    .and(item => item.userId === userId)
    .first();
  return entry || null;
}

/**
 * Reset all permanently/over-limit failed CTP actions back to pending.
 */
export async function resetFailedCtps() {
  const userId = requireUser();
  const failed = await db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.FAILED])
    .toArray();
  for (const entry of failed) {
    await db.ctpQueue.update(entry.id, {
      status: SyncStatus.PENDING,
      retryCount: 0,
      error: null,
    });
  }
}

/**
 * Clear synced CTP actions older than 24 hours.
 */
export async function cleanupSyncedCtps() {
  const userId = requireUser();
  const cutoff = Date.now() - (24 * 60 * 60 * 1000);
  return db.ctpQueue
    .where('[userId+status]')
    .equals([userId, SyncStatus.SYNCED])
    .filter(c => c.timestamp < cutoff)
    .delete();
}

/**
 * Cache event data for offline access
 */
export async function cacheEventData(event) {
  const userId = requireUser();
  return db.cachedEvents.put({
    ...event,
    userId,
    cachedAt: Date.now(),
  });
}

/**
 * Get cached event
 */
export async function getCachedEvent(eventId) {
  const userId = requireUser();
  const cached = await db.cachedEvents.get(eventId);
  // Only return if it belongs to current user
  return cached?.userId === userId ? cached : null;
}

/**
 * Cache group/pairing data
 */
export async function cacheGroupData(eventId, groupNumber, players) {
  const userId = requireUser();
  return db.cachedGroups.put({
    eventId,
    groupNumber,
    players,
    userId,
    cachedAt: Date.now(),
  });
}

/**
 * Get cached groups for event
 */
export async function getCachedGroups(eventId) {
  const userId = requireUser();
  return db.cachedGroups
    .where('eventId')
    .equals(eventId)
    .filter(g => g.userId === userId)
    .toArray();
}

/**
 * Cache player data
 */
export async function cachePlayerData(eventId, players) {
  const userId = requireUser();
  const now = Date.now();
  await db.transaction('rw', db.cachedPlayers, async () => {
    // Clear old cached players for this event and user
    await db.cachedPlayers
      .where('eventId')
      .equals(eventId)
      .filter(p => p.userId === userId)
      .delete();
    // Add new ones
    for (const player of players) {
      await db.cachedPlayers.add({
        ...player,
        eventId,
        userId,
        cachedAt: now,
      });
    }
  });
}

/**
 * Get cached players for event
 */
export async function getCachedPlayers(eventId) {
  const userId = requireUser();
  return db.cachedPlayers
    .where('eventId')
    .equals(eventId)
    .filter(p => p.userId === userId)
    .toArray();
}

/**
 * Cache scorecard data
 */
export async function cacheScorecardData(eventId, scorecard) {
  const userId = requireUser();
  return db.cachedScorecards.put({
    eventId,
    scorecard,
    userId,
    cachedAt: Date.now(),
  });
}

/**
 * Get cached scorecard
 */
export async function getCachedScorecard(eventId) {
  const userId = requireUser();
  const cached = await db.cachedScorecards.get(eventId);
  return cached?.userId === userId ? cached?.scorecard : null;
}

/**
 * Clear all cached data for the current user
 * Call this on logout to prevent data leakage
 */
export async function clearUserData() {
  if (!currentUserId) return;

  const userId = currentUserId;

  await db.transaction('rw', [
    db.syncQueue,
    db.ctpQueue,
    db.cachedEvents,
    db.cachedGroups,
    db.cachedPlayers,
    db.cachedScorecards,
  ], async () => {
    // Clear all user-specific data
    await db.syncQueue.filter(s => s.userId === userId).delete();
    await db.ctpQueue.filter(c => c.userId === userId).delete();
    await db.cachedEvents.filter(e => e.userId === userId).delete();
    await db.cachedGroups.filter(g => g.userId === userId).delete();
    await db.cachedPlayers.filter(p => p.userId === userId).delete();
    await db.cachedScorecards.filter(s => s.userId === userId).delete();
  });

  currentUserId = null;
}

/**
 * Clear all cached data (for debugging/reset)
 */
export async function clearAllCaches() {
  await db.transaction('rw', [
    db.cachedEvents,
    db.cachedGroups,
    db.cachedPlayers,
    db.cachedScorecards,
  ], async () => {
    await db.cachedEvents.clear();
    await db.cachedGroups.clear();
    await db.cachedPlayers.clear();
    await db.cachedScorecards.clear();
  });
}

// Note: migrateFromLocalStorage must be called after setCurrentUser
// so we don't auto-call it on module load anymore
